import { LocalizationHelperInstance } from "utils/helpers";
import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModel, WintSuperkollAPIModelExternalCompanyData } from "wint-api-client/dist/types";
import { store } from "store";
import { IconDefinitionConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import useAPIClientHelper from "./useAPIClientHelper";
import { CompanyService } from "services";

function useCompanyService() {
  const { apiClientInstance } = useAPIClientHelper();

  const SearchPersonsAutoComplete = (
    searchStr: string,
    excludeSelf?: boolean,
    excludeSalaryInactive?: boolean,
    excludeNotInBusinessUnitWithSalaryRights?: boolean
  ) => {
    return CompanyService.SearchPersons(
      searchStr,
      excludeSelf,
      excludeSalaryInactive,
      excludeNotInBusinessUnitWithSalaryRights
    ).then((result) =>
      result.map(
        (employee): AutocompleteListItem => ({
          id: employee.id,
          value: employee.name ?? "",
          icon: IconDefinitionConstants.WUser,
        })
      )
    );
  };

  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useTimeReportingPermissions)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const canReport = companyService.canLoggedInUserReportTime();
   *
   * @example
   * // New way (recommended):
   * function useTimeReportingPermissions() {
   *   const company = useSelector((state: AppState) => state.auth.selectedCompany);
   *
   *   const canLoggedInUserReportTime = () => {
   *     return (
   *       company.isTimeReportingEnabled &&
   *       company.loggedInUserIsAllowedToReportTime &&
   *       CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.Employee)
   *     );
   *   };
   *
   *   return { canLoggedInUserReportTime };
   * }
   *
   * // In component:
   * const { canLoggedInUserReportTime } = useTimeReportingPermissions();
   * const canReport = canLoggedInUserReportTime();
   */
  const canLoggedInUserReportTime = () => {
    const state = store.getState();
    return (
      state.auth.selectedCompany.isTimeReportingEnabled &&
      state.auth.selectedCompany.loggedInUserIsAllowedToReportTime &&
      CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.Employee)
    );
  };

  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useTimeReportingPermissions)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const canModify = companyService.permissionToModifyOtherPersonsTimeReports(personId);
   *
   * @example
   * // New way (recommended):
   * function useTimeReportingPermissions() {
   *   const company = useSelector((state: AppState) => state.auth.selectedCompany);
   *
   *   const permissionToModifyOtherPersonsTimeReports = (reportPersonId: number) => {
   *     if (!company.isTimeReportingEnabled) {
   *       return false;
   *     }
   *     // Move existing logic here using company state
   *   };
   *
   *   return { permissionToModifyOtherPersonsTimeReports };
   * }
   *
   * // In component:
   * const { permissionToModifyOtherPersonsTimeReports } = useTimeReportingPermissions();
   * const canModify = permissionToModifyOtherPersonsTimeReports(personId);
   */
  const permissionToModifyOtherPersonsTimeReports = (reportPersonId: number) => {
    const state = store.getState();

    if (!state.auth.selectedCompany.isTimeReportingEnabled) {
      return false;
    }
    const globalPermission =
      CompanyService.hasRelations([
        AuthEnums.PersonCompanyRelationType.Admin,
        AuthEnums.PersonCompanyRelationType.SalaryAdmin,
        AuthEnums.PersonCompanyRelationType.Invoices,
        AuthEnums.PersonCompanyRelationType.InvoicesAdmin,
      ]) ||
      state.auth.selectedCompany.loggedInUserIsBusinessUnitManager ||
      state.auth.selectedCompany.loggedInUserIsProjectManager;

    if (globalPermission) {
      return true;
    }

    return (
      reportPersonId === state.auth.loggedInUser.personId &&
      CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.Employee) &&
      state.auth.selectedCompany.loggedInUserIsAllowedToReportTime
    );
  };
  const getEmployeeName = async (id: number) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company).CompanyClient.GetEmployee(id);
    return res.data.name ?? "";
  };

  const getEmployees = async (filter: WintSuperkollAPIModel.PersonFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CompanyClient.GetEmployees(filter)
      .then((res) => res.data);
  };

  const getEmailAddresses = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CompanyClient.GetEmailAddresses()
      .then((res) => res.data);
  };

  const getCompanyBoardMembers = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CompanyClient.GetCompanyDirectors(id)
      .then((res) => res.data);
  };

  const updateCompanyBoardMembersDetails = async (
    id: number,
    directors: WintSuperkollAPIModelExternalCompanyData.Directors
  ) => {
    return await apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .CompanyClient.UpdateDirectorExtraData(id, directors)
      .then((res) => res.data);
  };

  const getExternalCompanyInformation = async (companyId: number) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company)
      .CompanyClient.GetExternalCompanyInformation(companyId)
      .then((res) => res.data);

    return res;
  };

  const getTextByVatType = (vatPeriod?: WintSuperkollAPIModelExternalCompanyData.VatPeriod) => {
    if (vatPeriod === undefined) return "";
    return LocalizationHelperInstance.getTranslatedEnumValue("Company.VatPeriodType", vatPeriod);
  };

  const getSelectedCompanyId = () =>
    apiClientInstance(AuthEnums.AuthType.User)
      .CompanyClient.GetSelectedCompanyId()
      .then((res) => res.data);

  const setSelectedCompanyId = (id: number) =>
    apiClientInstance(AuthEnums.AuthType.User).CompanyClient.SetSelectedCompanyId({ companyId: id });

  const getAvailableSalaryTypes = async (companyId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .CompanyClient.GetAvailableSalaryTypes(companyId)
      .then((res) => res.data);
  };

  return {
    SearchPersonsAutoComplete,
    canLoggedInUserReportTime,
    permissionToModifyOtherPersonsTimeReports,
    getEmployeeName,
    getCompanyBoardMembers,
    updateCompanyBoardMembersDetails,
    getExternalCompanyInformation,
    getTextByVatType,
    getEmployees,
    getEmailAddresses,
    getSelectedCompanyId,
    setSelectedCompanyId,
    getAvailableSalaryTypes,
  };
}

export default useCompanyService;
